'use client'

import { Badge, Box, BoxProps, Text } from '@chakra-ui/react'
import { ItemApi } from 'appTypes/items'
import { useMemo } from 'react'
import { removeDecimals } from 'utils/removeDecimals'
import PeriodsInfo from './PeriodsInfo'
import { ParsedPricingInfo } from 'utils/apiUtils/prices'
import { Location } from '@leid/api-locations'
import AboutLeidSmartCard from './AboutLeidSmartCard'
import { RentalTimeStats } from '../RentalTimeStats'
import { ProductOrigin } from '@leid/api-products'
import { useTranslation } from 'react-i18next'

type Props = BoxProps & {
  product: ItemApi
  location?: Location
  showExtraInfo?: boolean
  card?: boolean
}

const isLeidProduct = (product: ItemApi) => {
  return product.productOrigin === ProductOrigin.LEID_SMART || product.productOrigin === ProductOrigin.FRANCHISEE
}

const PriceInfo = ({ product, showExtraInfo, location, card = false, ...boxProps }: Props): JSX.Element | null => {
  const { t } = useTranslation('common')

  const getPricingInfoForLocation = (location: Location | undefined, pricingInfo: ParsedPricingInfo[] | undefined) => {
    if (!pricingInfo) {
      return undefined
    }

    if (!location) {
      return pricingInfo[0]
    }

    if (location.priceGroupId) {
      const info = pricingInfo.find((s) => s.priceGroupId === location.priceGroupId)
      if (info) {
        return info
      }
    }

    const defaultInfo = pricingInfo.find((s) => s.priceGroupId === undefined)
    if (defaultInfo) {
      return defaultInfo
    }
    return pricingInfo[0]
  }

  const pricingInfo = useMemo(
    () => getPricingInfoForLocation(location, product.pricingInfo),
    [location, product.pricingInfo],
  )

  if (!product.pricingInfo || product.pricingInfo.length === 0) {
    return null
  }

  return (
    <Box padding={2} borderRadius="lg" marginBottom={3} {...boxProps}>
      {pricingInfo && pricingInfo.minimalPrice > 0 && (
        <Text fontSize={card ? 'lg' : '2xl'} lineHeight="1.5" fontWeight="semibold">
          {t('from')} {removeDecimals(pricingInfo.minimalPrice)} {t('nok')}
        </Text>
      )}

      {isLeidProduct(product) && (
        <Badge colorPalette="green" size={card ? 'md' : 'lg'} padding={2} marginBottom={2}>
          15% {t('leidSmart.cashback')}
        </Badge>
      )}

      {showExtraInfo && pricingInfo && (
        <Box marginTop={10} marginBottom={10}>
          <PeriodsInfo pricingInfo={pricingInfo} />
        </Box>
      )}

      <RentalTimeStats product={product} />
      {!card && isLeidProduct(product) && <AboutLeidSmartCard />}
    </Box>
  )
}

export default PriceInfo
